import type { ConditionLifecycleRouteArgs } from './conditionLifecycleNavigation';
import {
  buildLifecycleControlPath,
  buildLifecycleEvidencePath,
  buildLifecycleImmediateIncidentPath,
  buildLifecyclePatternIncidentPath,
  buildLifecycleScanVerifyPath,
  buildLifecycleVisualPath,
} from './conditionLifecycleNavigation';
import {
  presentPatternAlertReason,
  presentPostControlPattern,
  resolveCanonicalControl,
  type PatternAlertReasonView,
} from './conditionLifecyclePresentation';
import type {
  ConditionLifecycleSummary,
  SiteConditionMeasurementWindow,
  SiteConditionState,
} from './siteConditionState';

export type VisualMarkerKind =
  | 'occurrence'
  | 'immediate'
  | 'pattern'
  | 'control'
  | 'scan';

export type VisualMarkerSide = 'before' | 'since' | 'unpivoted';

export type VisualMarkerSymbol = 'circle' | 'square' | 'triangle' | 'bar' | 'diamond';

export type VisualLifecycleOccurrence = {
  occurrence_id?: string;
  occurred_at?: string;
  area_id?: string | null;
  zone_id?: string | null;
  measurement_atomic_id?: string | null;
};

export type VisualLifecycleIncident = {
  incident_id?: string;
  trigger_kind?: string;
  ts_start?: string;
  severity?: string | null;
  observed_count?: number | null;
  envelope_metadata?: { min_events?: number; window_seconds?: number };
  source_occurrence_ids?: string[];
  threshold_occurrence_id?: string | null;
};

export type VisualLifecycleControl = {
  control_revision_id?: string;
  implemented_at?: string | null;
  implementation_state?: string | null;
  control_objective?: string | null;
};

export type VisualLifecycleScan = {
  scan_definition_id?: string;
  evaluated_at?: string | null;
  result_direction?: string | null;
  coverage_state?: string | null;
  finality?: string | null;
  before_report_id?: string | null;
  after_report_id?: string | null;
  window_start_utc?: string | null;
  window_end_utc?: string | null;
};

export type VisualLifecycleDetail = {
  condition_id?: string;
  condition_family?: string;
  canonical_scope?: {
    area_id?: string | null;
    zone_ids?: string[];
    camera_ids?: string[];
  };
  occurrences?: VisualLifecycleOccurrence[];
  incidents?: VisualLifecycleIncident[];
  controls?: VisualLifecycleControl[];
  scans?: VisualLifecycleScan[];
  since?: {
    anchor_started_at?: string | null;
    control_revision_id?: string | null;
    measurement?: unknown;
  };
};

export type VisualZoneGeometry = {
  id?: string;
  zone_id?: string;
  name?: string;
  polygon_norm?: number[][] | null;
  bbox_norm?: number[] | null;
};

export type VisualEvidenceState = {
  status: 'idle' | 'loading' | 'ready' | 'empty' | 'error';
  imageUrl: string | null;
  incidentId: string | null;
};

export type VisualLifecycleMarker = {
  kind: VisualMarkerKind;
  id: string;
  at: string;
  label: string;
  href: string | null;
  side: VisualMarkerSide;
  symbol: VisualMarkerSymbol;
  zoneId?: string | null;
  memberOccurrenceIds?: string[];
  thresholdOccurrenceId?: string | null;
  observedCount?: number | null;
  minEvents?: number | null;
  windowSeconds?: number | null;
};

export type SpatialMode = 'zone_geometry' | 'zone_named' | 'unavailable';

export type SpatialZoneView = {
  zoneId: string;
  name: string;
  polygonNorm: number[][] | null;
  bboxNorm: number[] | null;
  linkedOccurrenceCount: number | null;
  recurrenceValue: number | null;
  recurrenceUnits: string | null;
  recurrenceVisible: boolean;
  alertState: string;
  controlState: string;
  verificationState: string;
};

export type VisualTimeSemantics = {
  recurrenceRange: {
    key: string | null;
    start: string | null;
    end: string | null;
    value: number | null;
    units: string | null;
    summary: string;
  };
  patternWindow: {
    minEvents: number | null;
    windowSeconds: number | null;
    observedCount: number | null;
    summary: string;
  };
  sinceAnchor: {
    at: string | null;
    value: number | null;
    units: string | null;
    summary: string;
  };
  scanInterval: {
    beforeReportId: string | null;
    afterReportId: string | null;
    start: string | null;
    end: string | null;
    summary: string;
  };
};

export type VisualComparisonView = {
  hasControlPivot: boolean;
  recurrenceValue: number | null;
  recurrenceUnits: string | null;
  sinceValue: number | null;
  sinceUnits: string | null;
  patternAlertsBefore: number | null;
  patternAlertsSince: number;
  patternStatus: string;
  scanResult: string | null;
  coverageState: string | null;
  finality: string | null;
  coverageFinalityVisible: boolean;
};

export type VisualPartialStates = {
  offlineCamera: boolean;
  missingEvidence: boolean;
  insufficientCoverage: boolean;
  noRecurrence: boolean;
  noImmediate: boolean;
  noPattern: boolean;
  noControl: boolean;
  noScan: boolean;
  legacyPartial: boolean;
};

export type VisualLifecycleViewModel = {
  conditionFamily: string;
  areaLabel: string;
  cameraIds: string[];
  lifecycleOccurrenceCount: number | null;
  markers: VisualLifecycleMarker[];
  controlPivotAt: string | null;
  spatialMode: SpatialMode;
  spatialZones: SpatialZoneView[];
  spatialHonesty: string;
  timeSemantics: VisualTimeSemantics;
  comparison: VisualComparisonView;
  patternReason: PatternAlertReasonView | null;
  patternMembers: Array<{
    occurrenceId: string;
    occurredAt: string | null;
    zoneId: string | null;
    isThreshold: boolean;
  }>;
  evidenceHref: string | null;
  visualHref: string;
  partial: VisualPartialStates;
  evidence: VisualEvidenceState;
};

const MARKER_SYMBOL: Record<VisualMarkerKind, VisualMarkerSymbol> = {
  occurrence: 'circle',
  immediate: 'square',
  pattern: 'triangle',
  control: 'bar',
  scan: 'diamond',
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function parseMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(String(value));
  return Number.isFinite(ms) ? ms : null;
}

function markerSide(at: string, pivotAt: string | null): VisualMarkerSide {
  const pivotMs = parseMs(pivotAt);
  if (pivotMs == null) return 'unpivoted';
  const atMs = parseMs(at);
  if (atMs == null) return 'unpivoted';
  return atMs < pivotMs ? 'before' : 'since';
}

function pickRecurrenceWindow(
  condition: SiteConditionState | null,
): { key: string; window: SiteConditionMeasurementWindow } | null {
  const overTime = condition?.operationalMeasurements?.overTime || {};
  const entries = Object.entries(overTime);
  const preferred = entries.find(([, window]) => (
    Boolean(
      window?.recurrenceBasis?.windowStart
      || window?.operationalMeasurementProvenance?.windowStart
      || window?.summary?.measurementValue != null
    )
  ));
  if (preferred) return { key: preferred[0], window: preferred[1] };
  return entries[0] ? { key: entries[0][0], window: entries[0][1] } : null;
}

function windowRange(window: SiteConditionMeasurementWindow | null | undefined): {
  start: string | null;
  end: string | null;
} {
  if (!window) return { start: null, end: null };
  return {
    start: window.recurrenceBasis?.windowStart
      || window.operationalMeasurementProvenance?.windowStart
      || window.buckets[0]?.interval?.start
      || null,
    end: window.recurrenceBasis?.windowEnd
      || window.operationalMeasurementProvenance?.windowEnd
      || window.buckets[0]?.interval?.end
      || null,
  };
}

function windowValue(window: SiteConditionMeasurementWindow | null | undefined): {
  value: number | null;
  units: string | null;
} {
  if (!window) return { value: null, units: null };
  if (window.summary.measurementValue != null && Number.isFinite(window.summary.measurementValue)) {
    return {
      value: window.summary.measurementValue,
      units: window.basis?.units || window.recurrenceBasis?.basis || 'occurrences',
    };
  }
  const recorded = window.recurrenceBasis?.recordedOccurrenceTotal;
  if (recorded != null && Number.isFinite(recorded)) {
    return { value: recorded, units: 'occurrences' };
  }
  return { value: null, units: window.basis?.units || null };
}

function coverageInsufficient(condition: SiteConditionState | null, scanCoverage: string | null): boolean {
  if (text(scanCoverage).toLowerCase() === 'insufficient') return true;
  const now = condition?.operationalMeasurements?.now;
  const coverage = now?.summary?.measurementCoverage;
  if (coverage?.coverageGatePassed === false) return true;
  const blocked = (coverage?.blockedBy || []).map((item) => text(item).toLowerCase());
  return blocked.some((item) => item.includes('coverage') || item.includes('insufficient'));
}

function cameraLooksOffline(condition: SiteConditionState | null, explicit?: boolean): boolean {
  if (explicit) return true;
  const coverage = condition?.operationalMeasurements?.now?.summary?.measurementCoverage;
  if ((coverage?.telemetryMissingHours || 0) > 0 || coverage?.telemetryMissingSince) return true;
  const reasons = (condition?.currentActivity?.reasonCodes || []).map((item) => text(item).toLowerCase());
  return reasons.some((item) => item.includes('offline') || item.includes('telemetry_missing') || item.includes('camera_unavailable'));
}

function zoneName(zoneId: string, geometry: VisualZoneGeometry[]): string {
  const match = geometry.find((item) => text(item.id || item.zone_id) === zoneId);
  return text(match?.name) || zoneId;
}

function usablePolygon(value: unknown): number[][] | null {
  if (!Array.isArray(value) || value.length < 3) return null;
  const points = value.map((point) => {
    if (!Array.isArray(point) || point.length < 2) return null;
    const x = Number(point[0]);
    const y = Number(point[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return [x, y];
  }).filter((point): point is number[] => Boolean(point));
  return points.length >= 3 ? points : null;
}

export function buildVisualLifecycleModel(args: {
  lifecycle: ConditionLifecycleSummary | null;
  condition?: SiteConditionState | null;
  detail?: VisualLifecycleDetail | null;
  route: ConditionLifecycleRouteArgs & {
    incidentType?: string | null;
    conditionFamily?: string | null;
    safetyCaseId?: string | null;
    controlRecordPath?: string | null;
    legacyControlId?: string | null;
    legacyControlTitle?: string | null;
  };
  zones?: VisualZoneGeometry[];
  evidence?: VisualEvidenceState;
  cameraOffline?: boolean;
}): VisualLifecycleViewModel {
  const lifecycle = args.lifecycle;
  const condition = args.condition || null;
  const detail = args.detail || null;
  const route = args.route;
  const control = resolveCanonicalControl({
    lifecycle,
    legacyControlId: args.route.legacyControlId,
    legacyControlTitle: args.route.legacyControlTitle,
  });
  const pivotAt = control.authority === 'epic402' ? (lifecycle?.sinceStartedAt || null) : null;
  const scan = lifecycle?.latestScan || null;
  const pattern = lifecycle?.latestTemporalPatternIncident || null;
  const immediate = lifecycle?.latestImmediateIncident || null;
  const reason = presentPatternAlertReason(pattern);
  const postControl = presentPostControlPattern({
    sinceStartedAt: lifecycle?.sinceStartedAt,
    latestPattern: pattern,
    scan,
    backend: lifecycle?.postControlPattern,
  });
  const family = text(args.route.conditionFamily || condition?.conditionFamily || detail?.condition_family) || 'Linked condition';
  const areaId = text(route.areaId || condition?.canonicalScope?.areaId || detail?.canonical_scope?.area_id);
  const zoneIds = Array.from(new Set([
    ...((condition?.canonicalScope?.zoneIds || []).map(text).filter(Boolean)),
    ...((detail?.canonical_scope?.zone_ids || []).map(text).filter(Boolean)),
    text(route.zoneId),
    ...(detail?.occurrences || []).map((item) => text(item.zone_id)).filter(Boolean),
  ].filter(Boolean)));
  const cameraIds = Array.from(new Set([
    ...((condition?.canonicalScope?.cameraIds || []).map(text).filter(Boolean)),
    ...((detail?.canonical_scope?.camera_ids || []).map(text).filter(Boolean)),
    text(route.cameraId),
  ].filter(Boolean)));
  const recurrence = pickRecurrenceWindow(condition);
  const recurrenceMeasured = windowValue(recurrence?.window);
  const recurrenceRange = windowRange(recurrence?.window);
  const sinceMeasured = windowValue(condition?.operationalMeasurements?.sinceControl);
  const scanDetail = (detail?.scans || [])[0] || null;
  const markers: VisualLifecycleMarker[] = [];

  for (const occurrence of detail?.occurrences || []) {
    const id = text(occurrence.occurrence_id);
    const at = text(occurrence.occurred_at);
    if (!id || !at) continue;
    markers.push({
      kind: 'occurrence',
      id,
      at,
      label: 'Qualified occurrence',
      href: null,
      side: markerSide(at, pivotAt),
      symbol: MARKER_SYMBOL.occurrence,
      zoneId: text(occurrence.zone_id) || null,
    });
  }

  const incidents = detail?.incidents || [];
  const immediateRecords = incidents.filter((item) => text(item.trigger_kind).toUpperCase() === 'IMMEDIATE');
  const patternRecords = incidents.filter((item) => text(item.trigger_kind).toUpperCase() === 'TEMPORAL_PATTERN');
  const immediateSource = immediateRecords.length
    ? immediateRecords
    : (immediate ? [{
      incident_id: immediate.incidentId,
      trigger_kind: 'IMMEDIATE',
      ts_start: immediate.occurredAt || undefined,
      severity: immediate.severity,
    }] : []);
  const patternSource = patternRecords.length
    ? patternRecords
    : (pattern ? [{
      incident_id: pattern.incidentId,
      trigger_kind: 'TEMPORAL_PATTERN',
      ts_start: pattern.occurredAt || undefined,
      severity: pattern.severity,
      observed_count: pattern.observedCount,
      envelope_metadata: {
        min_events: pattern.patternMinEvents || undefined,
        window_seconds: pattern.patternWindowSeconds || undefined,
      },
      source_occurrence_ids: [],
      threshold_occurrence_id: null,
    }] : []);

  for (const incident of immediateSource) {
    const id = text(incident.incident_id);
    const at = text(incident.ts_start);
    if (!id || !at) continue;
    markers.push({
      kind: 'immediate',
      id,
      at,
      label: `Immediate alert${incident.severity ? ` · ${incident.severity}` : ''}`,
      href: buildLifecycleImmediateIncidentPath({
        ...route,
        incidentId: id,
        incidentType: args.route.incidentType,
        conditionFamily: family,
      }),
      side: markerSide(at, pivotAt),
      symbol: MARKER_SYMBOL.immediate,
    });
  }

  for (const incident of patternSource) {
    const id = text(incident.incident_id);
    const at = text(incident.ts_start);
    if (!id || !at) continue;
    markers.push({
      kind: 'pattern',
      id,
      at,
      label: 'Pattern Alert',
      href: buildLifecyclePatternIncidentPath({
        ...route,
        incidentId: id,
        incidentType: args.route.incidentType,
        conditionFamily: family,
      }),
      side: markerSide(at, pivotAt),
      symbol: MARKER_SYMBOL.pattern,
      memberOccurrenceIds: (incident.source_occurrence_ids || []).map(text).filter(Boolean),
      thresholdOccurrenceId: text(incident.threshold_occurrence_id) || null,
      observedCount: incident.observed_count ?? reason?.observedCount ?? null,
      minEvents: incident.envelope_metadata?.min_events ?? reason?.minEvents ?? null,
      windowSeconds: incident.envelope_metadata?.window_seconds ?? reason?.windowSeconds ?? null,
    });
  }

  if (control.authority === 'epic402' && pivotAt && control.controlRevisionId) {
    markers.push({
      kind: 'control',
      id: control.controlRevisionId,
      at: pivotAt,
      label: 'Control recorded',
      href: buildLifecycleControlPath({
        ...route,
        hasEpic402Control: true,
        controlRecordPath: args.route.controlRecordPath,
      }),
      side: 'since',
      symbol: MARKER_SYMBOL.control,
    });
  }

  const scanAt = text(scan?.evaluatedAt || scanDetail?.evaluated_at);
  if (scan && scanAt) {
    const reportId = scan.afterReportId || scanDetail?.after_report_id;
    markers.push({
      kind: 'scan',
      id: scan.scanDefinitionId || text(scanDetail?.scan_definition_id) || 'scan',
      at: scanAt,
      label: `Scan · ${scan.resultDirection || 'result pending'}`,
      href: buildLifecycleScanVerifyPath({
        ...route,
        reportId,
        safetyCaseId: args.route.safetyCaseId,
      }),
      side: markerSide(scanAt, pivotAt),
      symbol: MARKER_SYMBOL.scan,
    });
  }

  markers.sort((left, right) => (parseMs(left.at) || 0) - (parseMs(right.at) || 0));

  const geometry = args.zones || [];
  const spatialZones: SpatialZoneView[] = zoneIds.map((zoneId) => {
    const match = geometry.find((item) => text(item.id || item.zone_id) === zoneId);
    const polygon = usablePolygon(match?.polygon_norm);
    const bbox = Array.isArray(match?.bbox_norm) ? match?.bbox_norm as number[] : null;
    const linkedOccurrenceCount = detail
      ? (detail.occurrences || []).filter((item) => text(item.zone_id) === zoneId).length
      : null;
    return {
      zoneId,
      name: zoneName(zoneId, geometry),
      polygonNorm: polygon,
      bboxNorm: bbox && bbox.length >= 4 ? bbox : null,
      linkedOccurrenceCount,
      recurrenceValue: recurrenceMeasured.value,
      recurrenceUnits: recurrenceMeasured.units,
      recurrenceVisible: (recurrenceMeasured.value || 0) > 0,
      alertState: pattern ? 'Pattern Alert linked' : (immediate ? 'Immediate alert linked' : 'No alert linked'),
      controlState: control.authority === 'epic402'
        ? 'Control recorded'
        : (control.authority === 'legacy' ? 'Legacy Control only' : 'No Control recorded'),
      verificationState: scan
        ? `Scan ${scan.resultDirection || 'pending'} · coverage ${scan.coverageState || 'unavailable'}`
        : 'No Scan linked',
    };
  });
  const hasGeometry = spatialZones.some((zone) => zone.polygonNorm || zone.bboxNorm);
  const spatialMode: SpatialMode = spatialZones.length === 0
    ? 'unavailable'
    : (hasGeometry ? 'zone_geometry' : 'zone_named');

  const patternAlertsBefore = pivotAt
    ? markers.filter((item) => item.kind === 'pattern' && item.side === 'before').length
    : null;
  const insufficientCoverage = coverageInsufficient(condition, scan?.coverageState || null);
  const evidence = args.evidence || { status: 'idle', imageUrl: null, incidentId: null };
  const noRecurrence = (recurrenceMeasured.value == null || recurrenceMeasured.value === 0)
    && markers.filter((item) => item.kind === 'occurrence').length === 0;
  const evidenceHref = buildLifecycleEvidencePath({
    ...route,
    reportId: scan?.afterReportId,
    incidentId: scan?.motivatingIncidents[0]?.incidentId || pattern?.incidentId || immediate?.incidentId,
    incidentType: args.route.incidentType,
    conditionFamily: family,
    safetyCaseId: args.route.safetyCaseId,
  });

  const patternMembers = (patternSource[0]?.source_occurrence_ids || []).map((occurrenceId) => {
    const occ = (detail?.occurrences || []).find((item) => text(item.occurrence_id) === text(occurrenceId));
    return {
      occurrenceId: text(occurrenceId),
      occurredAt: occ?.occurred_at ? String(occ.occurred_at) : null,
      zoneId: occ?.zone_id ? String(occ.zone_id) : null,
      isThreshold: text(patternSource[0]?.threshold_occurrence_id) === text(occurrenceId),
    };
  });

  return {
    conditionFamily: family,
    areaLabel: areaId || (spatialZones[0]?.name || 'Area not named'),
    cameraIds,
    lifecycleOccurrenceCount: detail ? (detail.occurrences || []).length : null,
    markers,
    controlPivotAt: pivotAt,
    spatialMode,
    spatialZones,
    spatialHonesty: spatialMode === 'unavailable'
      ? 'No authoritative area or zone is linked, so no floor plan is shown.'
      : (spatialMode === 'zone_named'
        ? 'Zone names are authoritative. Precise floor positions are not available, so occurrence points are not drawn.'
        : 'Zone outlines use stored camera-normalized geometry. Occurrence coordinates are not implied.'),
    timeSemantics: {
      recurrenceRange: {
        key: recurrence?.key || null,
        start: recurrenceRange.start,
        end: recurrenceRange.end,
        value: recurrenceMeasured.value,
        units: recurrenceMeasured.units,
        summary: recurrenceRange.start
          ? `Recurrence is the Overview occurrence rate for ${recurrence?.key || 'the measured window'}, not a Pattern Alert.`
          : 'Recurrence is the Overview occurrence rate. It is not a Pattern Alert.',
      },
      patternWindow: {
        minEvents: reason?.minEvents ?? null,
        windowSeconds: reason?.windowSeconds ?? null,
        observedCount: reason?.observedCount ?? null,
        summary: reason
          ? `Pattern Alert rolling window: ${reason.minEvents ?? '—'} events in ${reason.windowSeconds ?? '—'}s.`
          : 'No Pattern Alert rolling window is linked.',
      },
      sinceAnchor: {
        at: pivotAt,
        value: sinceMeasured.value,
        units: sinceMeasured.units,
        summary: pivotAt
          ? 'Since is anchored at the recorded Control implementation time.'
          : 'No Control implementation time is linked, so Since cannot be used as a visual pivot.',
      },
      scanInterval: {
        beforeReportId: scan?.beforeReportId || null,
        afterReportId: scan?.afterReportId || null,
        start: scanDetail?.window_start_utc || null,
        end: scanDetail?.window_end_utc || null,
        summary: scan
          ? `Scan verification uses the before report and after report, not the Overview recurrence window.`
          : 'No Scan verification interval is linked.',
      },
    },
    comparison: {
      hasControlPivot: Boolean(pivotAt),
      recurrenceValue: recurrenceMeasured.value,
      recurrenceUnits: recurrenceMeasured.units,
      sinceValue: sinceMeasured.value,
      sinceUnits: sinceMeasured.units,
      patternAlertsBefore,
      patternAlertsSince: postControl.count,
      patternStatus: postControl.status,
      scanResult: scan?.resultDirection || null,
      coverageState: scan?.coverageState || postControl.coverageState,
      finality: scan?.finality || postControl.finality,
      coverageFinalityVisible: Boolean(scan?.coverageState || scan?.finality || postControl.coverageState || postControl.finality),
    },
    patternReason: reason,
    patternMembers,
    evidenceHref,
    visualHref: buildLifecycleVisualPath(route),
    partial: {
      offlineCamera: cameraLooksOffline(condition, args.cameraOffline),
      missingEvidence: evidence.status === 'empty' || evidence.status === 'error',
      insufficientCoverage,
      noRecurrence,
      noImmediate: !immediate && immediateSource.length === 0,
      noPattern: !pattern && patternSource.length === 0,
      noControl: control.authority === 'none',
      noScan: !scan,
      legacyPartial: control.authority === 'legacy' || (!lifecycle?.controlRevisionId && Boolean(scan && !scan.controlRevisionId)),
    },
    evidence,
  };
}

export function visualMarkerLegend(): Array<{ kind: VisualMarkerKind; symbol: VisualMarkerSymbol; label: string }> {
  return [
    { kind: 'occurrence', symbol: 'circle', label: 'Qualified occurrence' },
    { kind: 'immediate', symbol: 'square', label: 'Immediate alert' },
    { kind: 'pattern', symbol: 'triangle', label: 'Pattern Alert' },
    { kind: 'control', symbol: 'bar', label: 'Control' },
    { kind: 'scan', symbol: 'diamond', label: 'Scan / verification' },
  ];
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
